/**
 * Export the whole workspace — profile, answers, scenarios, checklist and the
 * uploaded files themselves — as one .tyche.json file the owner keeps, and
 * import it back on any device. This is how progress moves between browsers
 * until accounts exist.
 */

import { WorkspaceFormatError, migrate } from './migrations.js';
import type { Workspace } from './schema.js';

export const EXPORT_FORMAT = 'tyche-business-workspace';

export interface ExportFile {
  format: typeof EXPORT_FORMAT;
  exportedAt: string;
  workspace: Workspace;
  /** Uploaded files by document id, as data: URLs. */
  files: Record<string, string>;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}

export async function dataUrlToBlob(url: string): Promise<Blob> {
  const res = await fetch(url);
  return res.blob();
}

export async function buildExport(
  ws: Workspace,
  getBlob: (id: string) => Promise<Blob | undefined>,
): Promise<ExportFile> {
  const files: Record<string, string> = {};
  for (const d of ws.documents) {
    const b = await getBlob(d.id);
    if (b) files[d.id] = await blobToDataUrl(b);
  }
  return { format: EXPORT_FORMAT, exportedAt: new Date().toISOString(), workspace: ws, files };
}

/** Parse an imported file. Throws WorkspaceFormatError with a readable message. */
export function parseImport(text: string): { workspace: Workspace; files: Record<string, string> } {
  let obj: unknown;
  try {
    obj = JSON.parse(text);
  } catch {
    throw new WorkspaceFormatError('That file is not valid JSON.');
  }
  const o = obj as Partial<ExportFile>;
  if (!o || o.format !== EXPORT_FORMAT) {
    throw new WorkspaceFormatError('That file is not a Tyche Business workspace export.');
  }
  const files: Record<string, string> = {};
  for (const [k, v] of Object.entries(o.files ?? {})) {
    if (typeof v === 'string' && v.startsWith('data:')) files[k] = v;
  }
  return { workspace: migrate(o.workspace), files };
}
